import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LifeBuoy, Send, Phone, Mail, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { BRAND, fmtDT } from "@/lib/brand";
import { RequireSignIn, PageHead } from "@/components/site/PageBits";

const CATS = [["booking", "Booking"], ["payment", "Payment"], ["refund", "Refund"], ["damage", "Damage / accident"], ["kyc", "Driver verification"], ["account", "Account"], ["other", "Other / report a listing"]];
export const TICKET_STYLE = { OPEN: "bg-amber-100 text-amber-800", IN_PROGRESS: "bg-sky-100 text-sky-800", RESOLVED: "bg-emerald-100 text-emerald-800", CLOSED: "bg-neutral-200 text-neutral-700" };
const input = "w-full rounded-xl border border-hc-sage bg-white px-3.5 py-2.5 text-sm text-hc-ink outline-none focus:ring-2 focus:ring-hc-green";

function NewTicket({ onCreated, bookings }) {
  const [params] = useSearchParams();
  const car = params.get("car");
  const [f, setF] = useState({ category: car ? "other" : "booking", subject: car ? `Report listing: ${car}` : "", message: "", booking_id: "" });
  const [busy, setBusy] = useState(false);
  const api = useApi();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/tickets", f);
      toast.success(`Ticket ${r.data.id} raised — we'll reply here soon`);
      setF({ ...f, subject: "", message: "", booking_id: "" });
      onCreated(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="card-hc space-y-4 p-6" data-testid="ticket-form">
      <h2 className="font-display text-lg font-bold text-hc-ink">Raise a ticket</h2>
      <select value={f.category} onChange={set("category")} className={input} data-testid="ticket-category">{CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <select value={f.booking_id} onChange={set("booking_id")} className={input} data-testid="ticket-booking">
        <option value="">Not about a specific booking</option>
        {bookings.map((b) => <option key={b.id} value={b.id}>{b.id} · {b.car_name}</option>)}
      </select>
      <input value={f.subject} onChange={set("subject")} placeholder="Subject" required minLength={4} maxLength={120} className={input} data-testid="ticket-subject" />
      <textarea value={f.message} onChange={set("message")} placeholder="Tell us what happened" required minLength={5} rows={4} className={input} data-testid="ticket-message" />
      <button disabled={busy} className="btn-primary w-full" data-testid="ticket-submit">{busy ? "Sending…" : "Submit ticket"}</button>
    </form>
  );
}

function Thread({ t, onUpdate }) {
  const [msg, setMsg] = useState("");
  const api = useApi();
  const send = async (e) => {
    e.preventDefault();
    try {
      onUpdate((await api.post(`/tickets/${t.id}/messages`, { message: msg })).data);
      setMsg("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <details className="card-hc group p-5" data-testid={`ticket-${t.id}`}>
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2">
        <span><span className="font-semibold text-hc-ink">{t.subject}</span><span className="ml-2 text-xs text-hc-slate">{t.id} · {fmtDT(t.updated_at)}</span></span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${TICKET_STYLE[t.status]}`} data-testid={`ticket-status-${t.id}`}>{t.status.replace("_", " ")}</span>
      </summary>
      <div className="mt-4 space-y-3">
        {t.messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.from === "support" ? "bg-hc-mint text-hc-ink" : "ml-auto bg-hc-green text-white"}`}>
            <p className="whitespace-pre-wrap">{m.message}</p>
            <p className={`mt-1 text-[11px] ${m.from === "support" ? "text-hc-slate" : "text-white/70"}`}>{m.from === "support" ? "HooreCars Support" : "You"} · {fmtDT(m.at)}</p>
          </div>
        ))}
        {t.status !== "CLOSED" && (
          <form onSubmit={send} className="flex gap-2">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} required placeholder="Write a reply" className={input} data-testid={`ticket-reply-${t.id}`} />
            <button className="btn-primary !px-4" aria-label="Send" data-testid={`ticket-send-${t.id}`}><Send className="h-4 w-4" /></button>
          </form>
        )}
      </div>
    </details>
  );
}

function SupportInner() {
  const api = useApi();
  const [tickets, setTickets] = useState(null);
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, [api]);
  const upsert = (t) => setTickets((cur) => [t, ...(cur || []).filter((x) => x.id !== t.id)]);
  return (
    <div className="container-x py-10" data-testid="support-page">
      <PageHead icon={LifeBuoy} title="Help & support" sub="Raise a ticket and track replies from our team." />
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="card-hc flex items-center gap-3 p-4 text-sm" data-testid="support-call"><Phone className="h-5 w-5 text-hc-green" /><span><b>Call us</b><br />{BRAND.phone}</span></a>
        <a href={`mailto:${BRAND.email}`} className="card-hc flex items-center gap-3 p-4 text-sm" data-testid="support-email"><Mail className="h-5 w-5 text-hc-green" /><span><b>Email</b><br />{BRAND.email}</span></a>
        <a href="tel:112" className="flex items-center gap-3 rounded-2xl border border-hc-error/30 bg-rose-50 p-4 text-sm" data-testid="support-emergency"><AlertTriangle className="h-5 w-5 text-hc-error" /><span><b>Emergency on a trip?</b><br />Call 112, then call us</span></a>
      </div>
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <NewTicket onCreated={upsert} bookings={bookings} />
        <div className="space-y-3" data-testid="ticket-list">
          {tickets === null ? <div className="skeleton h-24" /> : tickets.length === 0
            ? <p className="rounded-2xl border border-dashed border-hc-sage bg-white p-10 text-center text-hc-slate" data-testid="tickets-empty">No tickets yet.</p>
            : tickets.map((t) => <Thread key={t.id} t={t} onUpdate={upsert} />)}
        </div>
      </div>
    </div>
  );
}

export default function Support() {
  return <RequireSignIn title="Help & support" testId="support-signin"><SupportInner /></RequireSignIn>;
}
